import type { JSX } from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { ActionButton } from "#/components/shared/buttons/ActionButtons";
import type { ArticleAsset, ArticleBlock } from "./types";

type RenderContext = {
  placements: {
    bySectionId: Map<string, ArticleAsset[]>;
    globalPlacements: ArticleAsset[];
  };
  allPlacements: ArticleAsset[];
  consumedPlacementIds: Set<string>;
  onSuggest?: (payload: {
    label: string;
    text: string;
    type: string;
    id?: string;
    key?: string | null;
  }) => void;
  onReviewAsset?: (payload: { assetId: string; basePrompt?: string }) => void;
};

function resolveAssetUrl(asset: ArticleAsset) {
  const placement = asset.placement as { assetUrl?: string | null } | null;
  return (
    asset.url ||
    placement?.assetUrl ||
    asset.asset?.url ||
    null
  );
}

function resolveAssetAlt(asset: ArticleAsset) {
  return asset.alt || asset.asset?.alt || "";
}

function wantsInlineLeftImage(asset: ArticleAsset) {
  const placement =
    asset.placement && typeof asset.placement === "object"
      ? (asset.placement as Record<string, unknown>)
      : {};
  const p = String(placement.placement || "").toLowerCase();
  const flow = String(placement.flow || "").toLowerCase();
  return p === "left" || flow === "side-by-side";
}

function resolveReviewAssetId(asset: ArticleAsset): string {
  return asset.assetId || asset.asset?.id || asset.id;
}

function resolveBasePrompt(asset: ArticleAsset): string | undefined {
  const candidates = [
    (asset as any).basePrompt,
    (asset as any).generation_prompt,
    (asset as any).generationPrompt,
    (asset as any).meta?.basePrompt,
    (asset as any).meta?.generationPrompt,
    (asset as any).meta?.generatedFromPrompt,
    (asset as any).asset?.basePrompt,
    (asset as any).asset?.generation_prompt,
    (asset as any).asset?.generationPrompt,
    (asset as any).asset?.meta?.basePrompt,
    (asset as any).asset?.meta?.generationPrompt,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim().length > 0) {
      return candidate.trim();
    }
  }
  return undefined;
}

function renderAsset(
  asset: ArticleAsset,
  onReviewAsset?: (payload: { assetId: string; basePrompt?: string }) => void
) {
  const url = resolveAssetUrl(asset);
  if (url) {
    const altText = resolveAssetAlt(asset);
    const showCaption = Boolean(altText) || Boolean(onReviewAsset);
    return (
      <figure key={asset.id} className="space-y-2">
        <img
          src={url}
          alt={altText}
          className="w-full rounded-lg border border-beige-200"
        />
        {showCaption ? (
          <figcaption className="flex flex-wrap items-center justify-between gap-2 text-xs text-brown-600">
            <span>{altText || ""}</span>
            {onReviewAsset ? (
              <ActionButton
                size="sm"
                tone="secondary"
                onClick={() =>
                  onReviewAsset({
                    assetId: resolveReviewAssetId(asset),
                    basePrompt: resolveBasePrompt(asset),
                  })
                }
              >
                Review with AI
              </ActionButton>
            ) : null}
          </figcaption>
        ) : null}
      </figure>
    );
  }

  const statusCandidates = [
    (asset as any).status,
    (asset as any).asset?.status,
    (asset as any).meta?.status,
    (asset as any).asset?.meta?.status,
  ]
    .filter((value) => typeof value === "string")
    .map((value) => String(value).toLowerCase());

  const isPending = statusCandidates.some((status) =>
    ["pending", "queued", "processing", "generating"].includes(status)
  );

  if (isPending) {
    return (
      <div
        key={asset.id}
        className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
      >
        Asset pending generation
      </div>
    );
  }

  return (
    <div
      key={asset.id}
      className="rounded-lg border border-beige-200 bg-cream-50 p-4 text-sm text-brown-600"
    >
      Unsupported asset: {asset.type ?? asset.id}
    </div>
  );
}

export function renderStandalonePlacements(
  placements: ArticleAsset[],
  context: RenderContext
) {
  return placements
    .filter((asset) => {
      const assetBlockId =
        asset.blockId ||
        (asset.asset?.meta as { blockId?: string } | undefined)?.blockId ||
        null;
      return !assetBlockId && !context.consumedPlacementIds.has(asset.id);
    })
    .map((asset) => {
      context.consumedPlacementIds.add(asset.id);
      return renderAsset(asset, context.onReviewAsset);
    });
}

export function renderBlockPlacements(blockId: string, context: RenderContext) {
  const placements = context.allPlacements.filter((asset) => {
    const assetBlockId =
      asset.blockId ||
      (asset.asset?.meta as { blockId?: string } | undefined)?.blockId ||
      null;

    return assetBlockId === blockId && !context.consumedPlacementIds.has(asset.id);
  });

  return placements.map((asset) => {
    context.consumedPlacementIds.add(asset.id);
    return renderAsset(asset, context.onReviewAsset);
  });
}

export function renderBlock({
  blocks,
  index,
  context,
}: {
  blocks: ArticleBlock[];
  index: number;
  context: RenderContext;
}): { elements: JSX.Element[]; skip: number } {
  const block = blocks[index];
  if (!block) {
    return { elements: [], skip: 0 };
  }

  const spanText =
    block.text_spans
      ?.map((span) => span.text)
      .filter((value): value is string => Boolean(value))
      .join("") ?? "";
  const resolvedText =
    spanText || block.text || block.content || block.markdown || "";

  const type = block.type?.toLowerCase() ?? "text";
  const elements: JSX.Element[] = [];
  const blockFigures = renderBlockPlacements(block.id, context);

  const wrapWithSuggestion = (
    element: JSX.Element,
    label: string,
    text: string,
    meta?: { type: string; id?: string; key?: string | null }
  ) => {
    if (!context.onSuggest) {
      return element;
    }

    return (
      <div key={`suggest-${block.id}`} className="group relative">
        {element}
        <button
          type="button"
          onClick={() =>
            context.onSuggest?.({
              label,
              text,
              type: meta?.type ?? "block",
              id: meta?.id,
              key: meta?.key ?? null,
            })
          }
          className="absolute right-2 top-2 rounded-full border border-beige-200 bg-white px-2 py-0.5 text-[10px] font-medium text-black shadow-sm opacity-0 transition-opacity group-hover:opacity-100"
        >
          Suggest
        </button>
      </div>
    );
  };

  if (type === "markdown" || type === "md") {
    elements.push(
      wrapWithSuggestion(
        <div key={block.id} className="prose max-w-none text-black">
          <ReactMarkdown rehypePlugins={[rehypeRaw]}>
            {block.markdown ?? block.content ?? ""}
          </ReactMarkdown>
        </div>,
        "Markdown",
        resolvedText,
        { type: "block", id: block.id, key: block.key ?? null }
      )
    );
    if (blockFigures.length) elements.push(...blockFigures);
    return { elements, skip: 0 };
  }

  if (type === "heading") {
    const Tag = block.level === 3 ? "h3" : "h2";
    elements.push(
      wrapWithSuggestion(
        <Tag key={block.id} className="text-2xl font-semibold text-black">
          {resolvedText}
        </Tag>,
        "Heading",
        resolvedText,
        { type: "block", id: block.id, key: block.key ?? null }
      )
    );
    if (blockFigures.length) elements.push(...blockFigures);
    return { elements, skip: 0 };
  }

  if (type === "paragraph") {
    elements.push(
      wrapWithSuggestion(
        <p key={block.id} className="text-base leading-relaxed text-black">
          {resolvedText}
        </p>,
        "Paragraph",
        resolvedText,
        { type: "block", id: block.id, key: block.key ?? null }
      )
    );
    if (blockFigures.length) elements.push(...blockFigures);
    return { elements, skip: 0 };
  }

  if (type === "list") {
    const items: string[] = [];
    for (let i = index + 1; i < blocks.length; i += 1) {
      const next = blocks[i];
      if (!next || next.type !== "list_item") break;
      const nextText =
        next.text_spans
          ?.map((span) => span.text)
          .filter((value): value is string => Boolean(value))
          .join("") ?? "";
      items.push(nextText || next.text || next.content || "");
    }

    const ordered = Boolean(block.data && (block.data as any).ordered);
    const ListTag = ordered ? "ol" : "ul";
    const listClassName = ordered
      ? "list-decimal space-y-2 pl-6 text-base text-brown-700"
      : "list-disc space-y-2 pl-6 text-base text-brown-700";
    elements.push(
      wrapWithSuggestion(
        <ListTag key={block.id} className={listClassName.replace("text-brown-700", "text-black")}>
          {items.map((item, itemIndex) => {
            const itemBlock = blocks[index + 1 + itemIndex];
            const itemBlockId = itemBlock?.id;
            const itemPlacements = itemBlockId
              ? context.allPlacements.filter((asset) => {
                  const assetBlockId =
                    asset.blockId ||
                    (asset.asset?.meta as { blockId?: string } | undefined)
                      ?.blockId ||
                    null;
                  return assetBlockId === itemBlockId;
                })
              : [];

            const inlineAsset = itemPlacements.find(
              (asset) => wantsInlineLeftImage(asset) && resolveAssetUrl(asset)
            );

            if (inlineAsset) {
              context.consumedPlacementIds.add(inlineAsset.id);
              return (
                <li key={`${block.id}-${itemIndex}`}>
                  <div className="grid grid-cols-[72px_1fr] items-start gap-3">
                    <img
                      src={resolveAssetUrl(inlineAsset)!}
                      alt={resolveAssetAlt(inlineAsset)}
                      className="h-[72px] w-[72px] rounded-md border border-beige-200 object-cover"
                    />
                    <span>{item}</span>
                  </div>
                </li>
              );
            }

            return <li key={`${block.id}-${itemIndex}`}>{item}</li>;
          })}
        </ListTag>,
        "List",
        items.join("\n"),
        { type: "block", id: block.id, key: block.key ?? null }
      )
    );
    if (blockFigures.length) elements.push(...blockFigures);
    return { elements, skip: items.length };
  }

  if (type === "list" && Array.isArray(block.items) && block.items.length > 0) {
    elements.push(
      wrapWithSuggestion(
        <ul key={block.id} className="list-disc space-y-2 pl-6 text-base text-black">
          {block.items.map((item, itemIndex) => (
            <li key={`${block.id}-${itemIndex}`}>{item}</li>
          ))}
        </ul>,
        "List",
        block.items.join("\n"),
        { type: "block", id: block.id, key: block.key ?? null }
      )
    );
    if (blockFigures.length) elements.push(...blockFigures);
    return { elements, skip: 0 };
  }

  elements.push(
    wrapWithSuggestion(
      <p key={block.id} className="text-base leading-relaxed text-black">
        {resolvedText}
      </p>,
      "Block",
      resolvedText,
      { type: "block", id: block.id, key: block.key ?? null }
    )
  );

  if (blockFigures.length) elements.push(...blockFigures);
  return { elements, skip: 0 };
}
